import { ViewChild, Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { Subject, Observable } from 'rxjs';
import { emptyProperty, emptyPropertyFinancial, Property, PropertyType } from '../../shared/models';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { getEligibleNewPropertyWorkspaces, getProperties, getPropertyTypes } from '../../property-store/property.selectors';
import { WorkspaceUserProperty } from '../../../shared';
import { Workspace } from '../../../shared';
import { SpUtilities } from '../../../../shared';
import { CollectionFrequency } from '../../../../shared';
import { AddPropertyDetailsComponent } from '../../components';
import { AddPropertyFinancialsComponent } from '../../components';
import {
  LoadEligibleNewPropertyWorkspacesAction,
  PropertyCreateAction
} from '../../property-store/property.actions';
import { getWorkspaceSettings } from '../../../workspace/workspace-store/workspace.selectors';
import { getUserProfile } from '../../../user/redux/user.selectors';
import { UserProfile } from '../../../user/models';
import { getSortedAdminWorkspaces } from 'app/internal/admin/admin-store/admin.selectors';
import { ReserveStudyModel } from '../../shared/models/property';
@Component({
  selector: 'sp-add-property',
  templateUrl: './add-property.html',
  standalone: false
})
export class AddPropertyComponent implements OnInit, OnDestroy {

  @ViewChild(ModalDirective) bsModal: ModalDirective;
  @ViewChild(AddPropertyDetailsComponent) addPropertyDetailsComponent: AddPropertyDetailsComponent;
  @ViewChild(AddPropertyFinancialsComponent) addPropertyFinancialsComponent: AddPropertyFinancialsComponent;
  @Input() workspaceId: string;
  properties$: Observable<WorkspaceUserProperty[]>;
  workspaces$: Observable<Workspace[]>;
  currentWorkspace$: Observable<Workspace>;
  propertyTypes$: Observable<PropertyType[]>;
  userProfile$: Observable<UserProfile>;
  states: string[];
  months: string[];
  collectionFrequencies: CollectionFrequency[];
  property: Property;
  formGroupDetails: UntypedFormGroup = new UntypedFormGroup({});
  formGroupFinancials: UntypedFormGroup = new UntypedFormGroup({});
  currentStep = 1;

  private ngOnDestroy$ = new Subject();

  constructor(private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private utils: SpUtilities) {
    this.properties$ = this.store.pipe(select(getProperties));
    this.currentWorkspace$ = this.store.pipe(select(getWorkspaceSettings));
    this.propertyTypes$ = this.store.pipe(select(getPropertyTypes));
    this.userProfile$ = this.store.pipe(select(getUserProfile));
  }

  ngOnInit() {

    if (this.workspaceId) {
      // Gets ALL Workspaces in the system.  Used on Admin > Portfolio area...
      this.workspaces$ = this.store.pipe(select(getSortedAdminWorkspaces));
    } else {
      // Gets only the Workspaces the current user has been invited/linked to.  Used in the Properties area...
      this.workspaces$ = this.store.pipe(select(getEligibleNewPropertyWorkspaces));
    }

    this.store.dispatch(new LoadEligibleNewPropertyWorkspacesAction());
    this.states = this.utils.getStates();
    this.months = this.utils.getMonthOfYearNames();
    this.collectionFrequencies = this.utils.getFrequencies();

    this.initForm();
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next('ngOnDestroy');
    this.ngOnDestroy$.unsubscribe();
  }

  onClose() {
    this.resetForm();
    this.bsModal.hide();
    this.currentStep = 1;
  }

  onSave() {

    const property = Object.assign({}, emptyProperty);
    property.workspaceId = this.workspaceId || this.formGroupDetails.controls['workspaceId'].value;
    property.isTest = this.formGroupDetails.controls['isTest'].value;
    property.propertyId = '';
    property.name = this.formGroupDetails.controls['name'].value;
    property.addressLine1 = this.formGroupDetails.controls['address1'].value;
    property.addressLine2 = this.formGroupDetails.controls['address2'].value;
    property.city = this.formGroupDetails.controls['city'].value;
    property.state = this.formGroupDetails.controls['state'].value;
    property.zipCode = this.formGroupDetails.controls['zipCode'].value;
    property.yearBuilt = this.formGroupDetails.controls['yearBuilt'].value;
    property.propertyTypeId = this.formGroupDetails.controls['propertyTypeId'].value;
    property.numberOfUnits = this.formGroupDetails.controls['numberOfUnits'].value;
    property.numberOfFloors = this.formGroupDetails.controls['numberOfFloors'].value;
    property.isCondoConverted = this.formGroupDetails.controls['conversionYesNo'].value;
    property.yearOfConversion = this.formGroupDetails.controls['yearOfConversion'].value;
    property.propertyDescription = this.formGroupDetails.controls['propertyDescription'].value;

    const propertyFinancial = Object.assign({}, emptyPropertyFinancial);
    propertyFinancial.propertyFinancialId = '';
    propertyFinancial.fiscalYearStartMonth = this.formGroupFinancials.controls['fiscalYearStartMonth'].value;
    propertyFinancial.upcomingExpenses = this.formGroupFinancials.controls['upcomingExpenses'].value;
    propertyFinancial.averageHoaAnnualFee = this.formGroupFinancials.controls['averageHoaAnnualFee'].value;
    propertyFinancial.feeCollectionFrequencyId = this.formGroupFinancials.controls['feeCollectionFrequencyId'].value;
    propertyFinancial.annualReserveContribution = this.formGroupFinancials.controls['annualReserveContribution'].value;
    propertyFinancial.yearEndReserveBalance = this.formGroupFinancials.controls['yearEndReserveBalance'].value;
    propertyFinancial.reserveMonthStart = this.formGroupFinancials.controls['reserveMonthStart'].value;
    propertyFinancial.inflationRate = this.formGroupFinancials.controls['inflationRate'].value;
    propertyFinancial.interestRate = this.formGroupFinancials.controls['interestRate'].value;
    propertyFinancial.reserveContributionIncreaseRate = this.formGroupFinancials.controls['reserveContributionIncreaseRate'].value;
    propertyFinancial.startingFiscalYear = +this.formGroupFinancials.controls['startingFiscalYear'].value;
    property.propertyFinancial = propertyFinancial;

    // Don't send a null upcoming expenses value
    if (!propertyFinancial.upcomingExpenses) {
      propertyFinancial.upcomingExpenses = 0;
    }

    // Reserve Study fields
    const reserveStudyDate = this.formGroupFinancials.controls['reserveStudyDate'].value;
    const reserveStudyTypeId = this.formGroupFinancials.controls['reserveStudyTypeId'].value;
    const reserveStudyProviderName = this.formGroupFinancials.controls['reserveStudyProviderName'].value;

    if (reserveStudyDate && reserveStudyTypeId) {
      const reserveStudy = <ReserveStudyModel>{};

      reserveStudy.reserveStudyDate = reserveStudyDate;
      reserveStudy.reserveStudyTypeId = +reserveStudyTypeId;
      reserveStudy.providerName = reserveStudyProviderName;

      property.reserveStudy = reserveStudy;
    }

    this.store.dispatch(new PropertyCreateAction(property));
  }

  onShow() {
    this.bsModal.show();
  }

  setStep(step: number) {
    window.scroll(0, 0);
    this.currentStep = step;
  }

  private initForm() {
    this.formGroupDetails = this.fb.group({});
    this.formGroupFinancials = this.fb.group({});
    this.property = Object.assign({}, emptyProperty);
  }

  private resetForm() {

    this.addPropertyDetailsComponent.resetForm();
    this.addPropertyFinancialsComponent.resetForm();
  }
}
